import { z } from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { practiceIdSchema, practiceMemberIdSchema, practiceMemberLicenseIdSchema } from './ids';
import {
    ehrPlatformsSchema as baseEhrPlatformsSchema,
    licenseTypeSchema as baseLicenseTypeSchema,
    stateSchema as baseStateSchema,
    taxonomyCodeSchema as baseTaxonomyCodeSchema,
    EHR_INSIGHTS_TYPE,
    guidanceKeySchema,
    onboardingStepsSchema,
    WHY_INSIGHTS_TYPE,
    welcomeGuideTasksSchema,
} from './practice-member.models';

export const PRACTICE_MEMBER_CREATE_SAMPLE_DATA_TYPE = 'practice-member/create-sample-data';
export const PRACTICE_MEMBER_UPDATE_TYPE = 'practice-member/update';
export const PRACTICE_MEMBER_UPDATE_INSIGHTS_TYPE = 'practice-member/update-insights';
export const PRACTICE_MEMBER_UPDATE_WELCOME_GUIDE_TYPE = 'practice-member/update-welcome-guide';
export const PRACTICE_MEMBER_ADD_LICENSE_TYPE = 'practice-member/add-license';
export const PRACTICE_MEMBER_UPDATE_LICENSE_TYPE = 'practice-member/update-license';
export const PRACTICE_MEMBER_REMOVE_LICENSE_TYPE = 'practice-member/remove-license';

export const MAX_INPUT_FIELD_LENGTH = 256;

const getErrorMessage = (fieldName: string) =>
    `${fieldName} must be at most ${MAX_INPUT_FIELD_LENGTH} characters`;

export const preferredNameSchema = z
    .string()
    .trim()
    .min(1, 'Enter first name')
    .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('First name'));
export const titleSchema = z.string().trim().max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Title'));
export const licenseNumberSchema = z
    .string()
    .trim()
    .min(1, 'Enter license number')
    .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('License number'));
export const npiSchema = z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Enter a 10-digit NPI');
export const licenseTypeSchema = z.enum(baseLicenseTypeSchema.options, {
    message: 'Select license type',
});
export const stateSchema = z.enum(baseStateSchema.options, {
    message: 'Select state',
});
// No message: taxonomy is optional and never blocks a save.
export const taxonomyCodeSchema = z.enum(baseTaxonomyCodeSchema.options);

export const practiceMemberUpdate = defineCommand(
    PRACTICE_MEMBER_UPDATE_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.recordId),
        preferredFirstName: preferredNameSchema.optional(),
        title: titleSchema.nullish(),
        legalName: z
            .string()
            .trim()
            .max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Legal name'))
            .nullish(),
        npi: npiSchema.nullish(),
    }),
    z.object({ id: z.uuid(), updatedAt: z.date() }),
);

// The id is minted in the domain, never accepted from the caller — unlike `clientCreate`, nothing
// here needs to mint one up front.
export const practiceMemberAddLicense = defineCommand(
    PRACTICE_MEMBER_ADD_LICENSE_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        type: licenseTypeSchema,
        number: licenseNumberSchema,
        state: stateSchema,
        taxonomyCode: taxonomyCodeSchema.optional(),
    }),
    z.object({ id: practiceMemberLicenseIdSchema.meta(metadata.nonSensitive) }),
);

// All four descriptive fields are required, not partial — the edit form always submits
// every field, and a partial update would need merge semantics the UI never exercises.
export const practiceMemberUpdateLicense = defineCommand(
    PRACTICE_MEMBER_UPDATE_LICENSE_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        licenseId: practiceMemberLicenseIdSchema.meta(metadata.recordId),
        type: licenseTypeSchema,
        number: licenseNumberSchema,
        state: stateSchema,
        taxonomyCode: taxonomyCodeSchema.optional(),
    }),
);

// No output: the caller refetches `practice-member/get-licenses` rather than
// reading a response body.
export const practiceMemberRemoveLicense = defineCommand(
    PRACTICE_MEMBER_REMOVE_LICENSE_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        licenseId: practiceMemberLicenseIdSchema.meta(metadata.recordId),
    }),
);

export const MAX_WHY_LENGTH = 1000;
export const MAX_EHR_NAME_LENGTH = 256;

// The response is optional, so it stays `.optional()` without a `.min(1)` — a blank or
// whitespace-only answer must never fail validation and block the member on Next.
export const whyInputSchema = z.object({
    type: z.literal(WHY_INSIGHTS_TYPE),
    customValue: z.string().trim().max(MAX_WHY_LENGTH).optional(),
});

export const ehrPlatformsInputSchema = z
    .object({
        type: z.literal(EHR_INSIGHTS_TYPE),
        selection: z.enum(baseEhrPlatformsSchema.options, { error: 'Choose an EHR' }),
        customValue: z.string().trim().min(1, 'Enter your EHR').max(MAX_EHR_NAME_LENGTH).optional(),
    })
    .superRefine((data, ctx) => {
        if (data.selection === 'OTHER' && !data.customValue) {
            ctx.addIssue({
                code: 'custom',
                path: ['customValue'],
                message: 'Enter your EHR',
            });
        }
    });

const insightsPayloadSchema = z.discriminatedUnion('type', [
    whyInputSchema,
    ehrPlatformsInputSchema,
]);

export const practiceMemberUpdateInsights = defineCommand(
    PRACTICE_MEMBER_UPDATE_INSIGHTS_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        payload: insightsPayloadSchema,
    }),
);

export const practiceMemberUpdateWelcomeGuide = defineCommand(
    PRACTICE_MEMBER_UPDATE_WELCOME_GUIDE_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        completedTasks: z.array(welcomeGuideTasksSchema).optional().meta(metadata.nonSensitive),
    }),
);

export const PRACTICE_MEMBER_UPDATE_ONBOARDING_STEP_TYPE = 'practice-member/update-onboarding-step';
export const PRACTICE_MEMBER_DELETE_ONBOARDING_TYPE = 'practice-member/delete-onboarding';
export const PRACTICE_MEMBER_DISMISS_GUIDANCE_TYPE = 'practice-member/dismiss-guidance';

export const practiceMemberUpdateOnboardingStep = defineCommand(
    PRACTICE_MEMBER_UPDATE_ONBOARDING_STEP_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        onboardingStep: onboardingStepsSchema.meta(metadata.nonSensitive),
    }),
);

export const practiceMemberDeleteOnboarding = defineCommand(
    PRACTICE_MEMBER_DELETE_ONBOARDING_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
);

export const practiceMemberDismissGuidance = defineCommand(
    PRACTICE_MEMBER_DISMISS_GUIDANCE_TYPE,
    z.object({
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        key: guidanceKeySchema.meta(metadata.nonSensitive),
    }),
);

export const practiceMemberCreateSampleData = defineCommand(
    PRACTICE_MEMBER_CREATE_SAMPLE_DATA_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
);
